
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const API_URL = "https://api.velormall.com/api/admin/user";

const UserInformation = () => {
    const { id } = useParams();

    const [data, setData] = useState({});
    const [userData, setUserData] = useState({});
    const [dataVulue, setDataVulue] = useState({});
    const [UserCheckValueData, setUserCheckValueData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showWithdrawalPassword, setShowWithdrawalPassword] = useState(false);

    const refSubmitDis = useRef(null);

    const fetchUserData = useCallback(async () => {
        try {
            setLoading(true);

            const res = await fetch(
                `${API_URL}/view/detail/${id}`
            );

            const result = await res.json();

            if (!res.ok || result.success === false) {
                throw new Error(result.message || "Failed to load user");
            }

            const user = result.data || {};
            setData(result);
            setUserData(user);

            setDataVulue({
                username: user.username ?? "",
                email: user.email ?? "",
                phone: user.phone ?? "",
                address: user.address ?? "",

                // Leave password fields empty for security.
                password: user.password,
                UserWithdrawalPassword: result?.UserWithdrawalPasswordData?.password,

                withdrawal_address: result.UserWithdrawalMethodData?.withdrawal_address ?? "",

                invitation_by:
                    (result.invitationCode || []).find(
                        (item) => item.invitation_code === result.data?.invitation_by
                    )?.invitation_code ?? "",
                city: user.city ?? "",
                state: user.state ?? "",
                zip_postal: user.zip_postal ?? "",
                country: user.country ?? "",
            });

            setUserCheckValueData({
                ev: Boolean(user.ev),
                mv: Boolean(user.mv),
                kv: Boolean(user.kv),
                withdrawal_password_status: Boolean(result?.UserWithdrawalPasswordData?.status),
            });
        } catch (error) {
            console.error("Fetch user error:", error);
            toast.error(error.message || "Failed to load user information");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchUserData();
        }
    }, [id, fetchUserData]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        setDataVulue((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const HandleCheckValue = (event) => {
        const { name, checked } = event.target;

        setUserCheckValueData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleUserSubmitData = async (event) => {
        event.preventDefault();

        if (saving) return;

        setSaving(true);

        const StoreData = {
            ...dataVulue,
            ...UserCheckValueData,
        };

        // Avoid submitting blank password fields.
        if (!StoreData.password) {
            delete StoreData.password;
        }

        if (!StoreData.UserWithdrawalPassword) {
            delete StoreData.UserWithdrawalPassword;
        }

        try {
            const res = await fetch(
                `${API_URL}/view/update/by/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(StoreData),
                }
            );

            const result = await res.json();

            if (!res.ok || result.success === false) {
                throw new Error(result.message || "Failed to update user");
            }

            toast.success(result.message || "User updated successfully");

            // Reload fresh values from the API.
            await fetchUserData();
        } catch (error) {
            console.error("Update user error:", error);
            toast.error(error.message || "Failed to update user");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <></>
        );
    }

    return (
        <div className="card mt-30">
            <div className="card-header">
                <h5 className="card-title mb-0">
                    Information of {userData?.name || userData?.username || "User"}
                </h5>
            </div>

            <div className="card-body">
                <form onSubmit={handleUserSubmitData}>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="username" className="required">
                                    User Name
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="username"
                                    id="username"
                                    required
                                    value={dataVulue.username ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="email" className="required">
                                    Email
                                </label>
                                <input
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    id="email"
                                    required
                                    value={dataVulue.email ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="mobile" className="required">
                                    Mobile Number
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="phone"
                                    id="mobile"
                                    required
                                    value={dataVulue.phone ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="address">Address</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="address"
                                    id="address"
                                    value={dataVulue.address ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="password">Login Password</label>

                                <div className="position-relative">
                                    <input
                                        className="form-control"
                                        type={showLoginPassword ? "text" : "password"}
                                        name="password"
                                        id="password"
                                        placeholder="Enter new password"
                                        autoComplete="new-password"
                                        value={dataVulue.password ?? ""}
                                        onChange={handleInputChange}
                                        style={{ paddingRight: "45px" }}
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowLoginPassword((prev) => !prev)
                                        }
                                        className="btn p-0 border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-3"
                                        aria-label={
                                            showLoginPassword
                                                ? "Hide login password"
                                                : "Show login password"
                                        }
                                    >
                                        {showLoginPassword ? (
                                            <EyeOff size={18} />
                                        ) : (
                                            <Eye size={18} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="withdrawalPassword">
                                    Withdrawal Password
                                </label>

                                <div className="position-relative">
                                    <input
                                        className="form-control"
                                        type={
                                            showWithdrawalPassword ? "text" : "password"
                                        }
                                        name="UserWithdrawalPassword"
                                        id="withdrawalPassword"
                                        placeholder="Enter new withdrawal password"
                                        autoComplete="new-password"
                                        value={
                                            dataVulue.UserWithdrawalPassword ?? ""
                                        }
                                        onChange={handleInputChange}
                                        style={{ paddingRight: "45px" }}
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowWithdrawalPassword((prev) => !prev)
                                        }
                                        className="btn p-0 border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-3"
                                        aria-label={
                                            showWithdrawalPassword
                                                ? "Hide withdrawal password"
                                                : "Show withdrawal password"
                                        }
                                    >
                                        {showWithdrawalPassword ? (
                                            <EyeOff size={18} />
                                        ) : (
                                            <Eye size={18} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="withdrawal_address">
                                    Withdrawal Address
                                </label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="withdrawal_address"
                                    id="withdrawal_address"
                                    value={dataVulue.withdrawal_address ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="invitation_by">Referral By</label>
                                <select
                                    className="form-control"
                                    name="invitation_by"
                                    id="invitation_by"
                                    value={dataVulue.invitation_by ?? ""}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Referral Code</option>

                                    {data?.invitationCode?.map((item) => (
                                        <option
                                            key={item.invitation_code}
                                            value={item.invitation_code}
                                        >
                                            {item.invitation_code}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="city">City</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="city"
                                    id="city"
                                    value={dataVulue.city ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="state">State</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="state"
                                    id="state"
                                    value={dataVulue.state ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="zip">Zip/Postal</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="zip_postal"
                                    id="zip"
                                    value={dataVulue.zip_postal ?? ""}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-xl-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="country">Country</label>
                                <select
                                    name="country"
                                    className="form-control"
                                    id="country"
                                    value={dataVulue.country ?? ""}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Country</option>
                                    <option value="AF">Afghanistan</option>
                                    <option value="AX">Aland Islands</option>
                                    <option value="AL">Albania</option>
                                    <option value="DZ">Algeria</option>
                                    <option value="AS">American Samoa</option>
                                    <option value="AD">Andorra</option>
                                    <option value="AO">Angola</option>
                                    <option value="AR">Argentina</option>
                                    <option value="AU">Australia</option>
                                    <option value="BD">Bangladesh</option>
                                    <option value="BE">Belgium</option>
                                    <option value="BR">Brazil</option>
                                    <option value="CA">Canada</option>
                                    <option value="CN">China</option>
                                    <option value="FR">France</option>
                                    <option value="DE">Germany</option>
                                    <option value="IN">India</option>
                                    <option value="ID">Indonesia</option>
                                    <option value="JP">Japan</option>
                                    <option value="MY">Malaysia</option>
                                    <option value="NP">Nepal</option>
                                    <option value="PK">Pakistan</option>
                                    <option value="SA">Saudi Arabia</option>
                                    <option value="SG">Singapore</option>
                                    <option value="LK">Sri Lanka</option>
                                    <option value="TH">Thailand</option>
                                    <option value="AE">United Arab Emirates</option>
                                    <option value="GB">United Kingdom</option>
                                    <option value="US">United States</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="form-group col-xl-3 col-md-6 col-12">
                            <label htmlFor="ev">Email Verification</label>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="ev"
                                    id="ev"
                                    onChange={HandleCheckValue}
                                    checked={Boolean(UserCheckValueData.ev)}
                                />
                            </div>
                        </div>

                        <div className="form-group col-xl-3 col-md-6 col-12">
                            <label htmlFor="mv">Mobile Verification</label>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="mv"
                                    id="mv"
                                    onChange={HandleCheckValue}
                                    checked={Boolean(UserCheckValueData.mv)}
                                />
                            </div>
                        </div>

                        <div className="form-group col-xl-3 col-md-6 col-12">
                            <label htmlFor="kv">KYC</label>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="kv"
                                    id="kv"
                                    onChange={HandleCheckValue}
                                    checked={Boolean(UserCheckValueData.kv)}
                                />
                            </div>
                        </div>
                        <div className="form-group col-xl-3 col-md-6 col-12">
                            <label htmlFor="withdrawal_password_status">Withdrawal password
                                <br />
                               ({UserCheckValueData.withdrawal_password_status ?
                                    <span >active</span>
                                    :
                                    <span className="text-danger">Inactive</span>
                                })


                            </label>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="withdrawal_password_status"
                                    id="withdrawal_password_status"
                                    onChange={HandleCheckValue}
                                    checked={Boolean(UserCheckValueData.withdrawal_password_status)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-12">
                            <div className="form-group">
                                <button
                                    ref={refSubmitDis}
                                    type="submit"
                                    className="btn btn-primary w-100 h-45"
                                    disabled={saving}
                                >
                                    {saving ? "Updating..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserInformation;
